import { RouteProp, useRoute } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { useShallow } from 'zustand/shallow';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useSendPasswordResetEmail } from '@/features/auth/queries';
import { useUnAuthStackNavigation } from '@/hooks';
import { captureException } from '@/libs/logger';
import { UnAuthStackParamList } from '@/navigations/types';
import { useUiStore } from '@/stores';

/** Resend cooldown in seconds — the countdown starts here when the screen loads. */
const RESEND_COOLDOWN_SECONDS = 60 * 3;

export function useForgotPasswordLinkSent() {
  const navigation = useUnAuthStackNavigation();
  const { email } = useRoute<RouteProp<UnAuthStackParamList, 'ForgotPasswordLinkSent'>>().params;
  const { mutateAsync: sendResetEmail } = useSendPasswordResetEmail();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Absolute end time (epoch ms). Deriving the remaining seconds from this instead of
  // decrementing a counter keeps the countdown correct across background → foreground,
  // where JS timers are throttled/paused by iOS and a plain `prev - 1` would fall behind.
  const endTimeRef = useRef<number>(0);

  // Recompute `secondsLeft` from the absolute end time, and stop the interval when done.
  const syncSecondsLeft = useCallback(() => {
    const remaining = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
    setSecondsLeft(remaining);
    if (remaining <= 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    endTimeRef.current = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
    setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    intervalRef.current = setInterval(syncSecondsLeft, 1000);
  }, [syncSecondsLeft]);

  // Start the countdown when the screen loads; clear the interval on unmount.
  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startCountdown]);

  // Timers are throttled while backgrounded, so re-sync from the absolute end time the
  // moment the app becomes active again to correct any drift.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') {
        syncSecondsLeft();
      }
    });
    return () => subscription.remove();
  }, [syncSecondsLeft]);

  const handleResend = useCallback(async () => {
    try {
      showGlobalLoading();
      await sendResetEmail(email);
      startCountdown();
      showAlert({
        title: STRINGS.auth.forgotPassword.noticeTitle,
        message: STRINGS.auth.forgotPassword.resentSuccess,
      });
    } catch (error) {
      captureException(
        { error_payload: error },
        { tags: { usk_log_type: 'handle_resend_reset_email' } },
      );
    } finally {
      hideGlobalLoading();
    }
  }, [showGlobalLoading, sendResetEmail, email, startCountdown, hideGlobalLoading]);

  const handleBackToLogin = useCallback(() => {
    navigation.popTo('Login');
  }, [navigation]);

  return {
    email,
    secondsLeft,
    /** Cooldown still running → the timer is shown and "Resend" is disabled. */
    isCountingDown: secondsLeft > 0,
    handleResend,
    handleBackToLogin,
  };
}
